import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslate } from "../../../../hooks/LanguageProvider";

interface FleetCo2Props {
  data?: {
    emissions?: number[];
    categories?: string[];
    target?: number;
    total?: number;
  };
  periodHours?: number;
  title?: string;
}

function toNumbers(values: unknown) {
  return Array.isArray(values) ? values.map((value) => Number(value) || 0) : [];
}

const FleetCo2: React.FC<FleetCo2Props> = ({ data, periodHours = 24, title = "CO2" }) => {
  const { translate } = useTranslate();
  const [refreshKey, setRefreshKey] = useState(0);
  const emissions = toNumbers(data?.emissions);
  const configuredCategories = data?.categories;
  const categories = Array.isArray(configuredCategories) && configuredCategories.length
    ? configuredCategories.map(String)
    : ["0-6h", "6-12h", "12-18h", "18-24h"];
  const chartValues = emissions.length ? emissions : [12, 18, 9, 14];
  const total = Number(data?.total ?? chartValues.reduce((sum, value) => sum + value, 0));
  const target = Number(data?.target ?? Math.max(1, total * 0.85));

  const options: Highcharts.Options = useMemo(() => ({
    chart: {
      type: "column",
      height: 260,
      backgroundColor: "transparent",
      spacing: [6, 0, 0, 0],
    },
    credits: { enabled: false },
    title: { text: undefined },
    xAxis: {
      categories,
      lineColor: "#e5e7eb",
      tickColor: "#e5e7eb",
    },
    yAxis: {
      min: 0,
      title: { text: "Kg" },
      gridLineColor: "#eef2f7",
      plotLines: [{
        color: "#f59e0b",
        width: 2,
        value: target,
        dashStyle: "ShortDash",
        label: { text: translate("Objectif"), style: { color: "#92400e" } },
      }],
    },
    legend: { enabled: false },
    tooltip: { pointFormat: "<b>{point.y:.1f} Kg CO2</b>" },
    plotOptions: {
      column: {
        borderRadius: 5,
        colorByPoint: true,
        colors: ["#2563eb", "#18b26b", "#f59e0b", "#dc2626"],
      },
    },
    series: [{
      type: "column",
      name: "CO2",
      data: chartValues,
    }],
  }), [categories.join("|"), chartValues.join("|"), target, translate, refreshKey]);

  return (
    <div className="dash2-chart-widget dash2-co2-widget">
      <div className="dash2-chart-header">
        <div>
          <span className="dash2-chart-kicker">{periodHours}h</span>
          <h3>{title || translate("CO2")}</h3>
        </div>
        <div className="dash2-chart-actions">
          <span className="dash2-chart-pill">{total.toFixed(1)} Kg</span>
          <button type="button" className="dash2-chart-refresh" onClick={() => setRefreshKey((key) => key + 1)} title={translate("Refresh")}>
            <i className="las la-sync" />
          </button>
        </div>
      </div>
      <div className="dash2-chart-metrics">
        <span><strong>{total.toFixed(1)}</strong>Total Kg</span>
        <span><strong>{target.toFixed(1)}</strong>{translate("Objectif")}</span>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default FleetCo2;
